import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  AlertTitle,
  CircularProgress,
  List,
  ListItem,
  Chip,
  TextField,
  IconButton,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { chapterPipeline, describeError, getVideo, messageOf, patchVideo, unwrap } from '../admin/api';
import { formatTime, parseTimeInput } from '../ps/format';
import { staleFlags, stalePatch } from '../admin/schema';

// Chapters are derived server-side from the video's registered English
// transcript. Suggestions are never written without an explicit second step --
// unlike subtitles, a wrong chapter title is visible on every play.

const EMPTY = { open: false, loading: false, error: '', gate: null, result: null, registered: false };

// Editing is text-first, but a row the admin never touched must keep its exact
// stored value -- formatTime is second-resolution and would quietly round a
// stored 16.726882 down to 16 on every save.
//
// `pristine` marks the rows that have a stored value worth preserving. It is a
// separate flag rather than a sentinel time, because every sentinel that
// formats to a real timestamp is a time a chapter can legitimately have -- a
// row added at the playhead at 00:00 formats identically to a stored 0.
function toDraft(chapters) {
  return chapters.map((c, i) => ({
    key: `${i}-${c.time}`,
    pristine: true,
    time: Number(c.time) || 0,
    timeText: formatTime(c.time),
    title: c.title || '',
  }));
}

// The stored number stays authoritative only while the row is untouched and
// its text still renders to the same thing.
function resolveTime(row) {
  return row.pristine && row.timeText === formatTime(row.time)
    ? row.time
    : parseTimeInput(row.timeText);
}

function draftToChapters(draft) {
  return draft
    .map((row) => ({ time: resolveTime(row), title: row.title.trim() }))
    .sort((a, b) => a.time - b.time);
}

function validateDraft(draft) {
  const rows = draft.map((row) => ({ ...row, parsed: resolveTime(row) }));

  if (!rows.length) return 'Add at least one chapter, or delete them all and save from an empty list.';
  const badTime = rows.find((r) => r.parsed === null);
  if (badTime) return `"${badTime.timeText}" is not a valid time. Use MM:SS or a number of seconds.`;
  if (rows.some((r) => !r.title.trim())) return 'Every chapter needs a title.';

  const times = rows.map((r) => Math.round(r.parsed));
  if (new Set(times).size !== times.length) return 'Two chapters share the same time.';
  return '';
}

function ChapterList({ chapters, dense }) {
  return (
    <List dense={dense} disablePadding>
      {chapters.map((c, i) => (
        <ListItem key={`${c.time}-${i}`} disableGutters sx={{ py: 0.25 }}>
          <Typography variant="body2" sx={{ minWidth: 64, fontFamily: 'monospace', color: 'text.secondary' }}>
            {formatTime(c.time)}
          </Typography>
          <Typography variant="body2">{c.title}</Typography>
        </ListItem>
      ))}
    </List>
  );
}

// `playhead` is where the preview player currently is, so a chapter can be
// marked against what is on screen. This is the surviving half of the second
// chapter editor that used to live inside the player and wrote straight to the
// API; here it only adds a row to the draft, which still has to be saved.
function ChapterManager({ videoData, videoIdentifier, onUpdateVideoData, onNotify = () => {}, playhead = null }) {
  const [state, setState] = useState(EMPTY);
  const [draft, setDraft] = useState(null);
  const [draftError, setDraftError] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);

  const existing = videoData.chapters || [];
  const stale = staleFlags(videoData);

  // Regenerating clears this half of the staleness without touching the other:
  // new chapters say nothing about whether the subtitles are still valid.
  const clearStale = () =>
    patchVideo(videoIdentifier, stalePatch({ ...stale, chapters: false })).catch(() => null);

  const refreshVideoData = async () => {
    try {
      if (stale.chapters) await clearStale();
      onUpdateVideoData(await getVideo(videoIdentifier));
    } catch (error) {
      onNotify('Chapters were registered, but the view could not refresh.', 'warning');
    }
  };

  const addRow = (seconds) =>
    setDraft((prev) => [
      ...(prev || []),
      {
        key: `new-${(prev || []).length}-${Math.random()}`,
        // Not pristine: this row has no stored value, so its text is the only
        // source of truth even when it reads as a perfectly ordinary time.
        pristine: false,
        time: 0,
        timeText: seconds == null ? '' : formatTime(seconds),
        title: '',
      },
    ]);

  // register=false previews; register=true writes. `force` clears a 422 gate
  // the admin has read and accepted.
  const run = async ({ register, force = false }) => {
    setState((prev) => ({ ...prev, loading: true, error: '', gate: null }));

    try {
      // Reading and summarising a full transcript takes tens of seconds; the
      // client carries the timeout and the 422-is-not-an-error handling.
      const { status, data } = await chapterPipeline({ identifier: videoIdentifier, register, force });

      if (status === 200) {
        const payload = unwrap(data);
        setState((prev) => ({
          ...prev,
          loading: false,
          result: payload,
          registered: !!payload?.registered,
        }));
        if (payload?.registered) {
          await refreshVideoData();
          onNotify(`${payload.chapters?.length || 0} chapters registered.`, 'success');
        }
        return;
      }

      if (status === 422) {
        const payload = unwrap(data);
        setState((prev) => ({
          ...prev,
          loading: false,
          gate: {
            // "too short" carries a duration; the overwrite gate carries the
            // existing chapters alongside the proposal.
            kind: payload?.existing_chapters ? 'overwrite' : 'short',
            message: messageOf(data),
            duration: payload?.duration,
            chapters: payload?.chapters,
            existingChapters: payload?.existing_chapters,
            register,
          },
        }));
        return;
      }

      const message =
        status === 401
          ? 'Wrong password — nothing was written.'
          : messageOf(data) || `The pipeline returned ${status}.`;
      setState((prev) => ({ ...prev, loading: false, error: message }));
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false, error: `Could not reach the pipeline: ${error.message}` }));
    }
  };

  const updateRow = (index, patch) =>
    setDraft((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const saveDraft = async () => {
    const problem = validateDraft(draft);
    if (problem) {
      setDraftError(problem);
      return;
    }

    setSavingDraft(true);
    setDraftError('');
    try {
      // addupdate merges, so sending only chapters leaves every other field of
      // the video record untouched.
      onUpdateVideoData(
        await patchVideo(videoIdentifier, {
          chapters: draftToChapters(draft),
          // Hand-edited times are as good as regenerated ones: the admin has
          // just looked at every row against the current file.
          ...(stale.chapters ? stalePatch({ ...stale, chapters: false }) : {}),
        }),
      );
      setDraft(null);
      onNotify('Chapters saved.', 'success');
    } catch (error) {
      setDraftError(describeError(error, 'Nothing was saved.'));
    } finally {
      setSavingDraft(false);
    }
  };

  const proposed = state.gate?.chapters || state.result?.chapters || [];
  const offsetStripped = state.result?.timecode_offset_stripped;

  return (
    <Box>
      {stale.chapters && !draft && (
        <Alert severity="warning" sx={{ mb: '10px' }}>
          The video file was replaced with a different timeline — these chapters are
          probably out of date. Suggest new ones, or edit the times by hand; either
          clears this warning.
        </Alert>
      )}

      {draft ? (
        <Box>
          {draft.map((row, index) => (
            <Box key={row.key} sx={{ display: 'flex', gap: '4px', alignItems: 'center', mb: '4px' }}>
              <TextField
                size="small"
                value={row.timeText}
                placeholder="00:00"
                onChange={(e) => updateRow(index, { timeText: e.target.value })}
                sx={{ width: 92 }}
                inputProps={{ 'aria-label': `Chapter ${index + 1} time`, className: 'admin-mono' }}
              />
              <TextField
                size="small"
                fullWidth
                placeholder="TITLE"
                value={row.title}
                onChange={(e) => updateRow(index, { title: e.target.value.toUpperCase() })}
                inputProps={{ 'aria-label': `Chapter ${index + 1} title` }}
              />
              <IconButton
                size="small"
                aria-label={`Remove chapter ${index + 1}`}
                onClick={() => setDraft((prev) => prev.filter((_, i) => i !== index))}
                sx={{ '&:hover': { color: 'var(--ps-mute)' } }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}

          {draftError && <Alert severity="error" sx={{ my: '10px' }}>{draftError}</Alert>}

          <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mt: '10px' }}>
            <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => addRow(null)}>
              Add chapter
            </Button>
            {playhead != null && (
              <Button
                size="small"
                startIcon={<MyLocationIcon sx={{ fontSize: 14 }} />}
                onClick={() => addRow(playhead)}
              >
                At playhead {formatTime(playhead)}
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => { setDraft(null); setDraftError(''); }} disabled={savingDraft}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={saveDraft} disabled={savingDraft}>
              {savingDraft ? 'Saving…' : 'Save chapters'}
            </Button>
          </Box>
          <Typography sx={{ fontSize: 10.5, color: 'var(--ps-text-dim)', mt: '10px' }}>
            Times accept MM:SS, H:MM:SS or a number of seconds. Chapters are sorted on save.
          </Typography>
        </Box>
      ) : (
        <>
          {existing.length > 0 ? (
            <ChapterList chapters={existing} dense />
          ) : (
            <Typography variant="body2" sx={{ color: 'var(--ps-text-dim)' }}>
              No chapters yet.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mt: '18px' }}>
            <Button
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
              onClick={() => setState({ ...EMPTY, open: true })}
            >
              Suggest chapters
            </Button>
            <Button
              startIcon={<EditIcon sx={{ fontSize: 15 }} />}
              onClick={() => { setDraft(toDraft(existing)); setDraftError(''); }}
            >
              {existing.length ? 'Edit chapters' : 'Add manually'}
            </Button>
          </Box>
        </>
      )}

      <Dialog
        open={state.open}
        onClose={state.loading ? undefined : () => setState(EMPTY)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Suggest chapters</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            Chapters are derived from this video's registered English subtitle track.
            Nothing is written until you register the suggestions.
          </DialogContentText>

          {state.loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Reading the transcript…
              </Typography>
            </Box>
          )}

          {state.error && <Alert severity="error" sx={{ mt: 2 }}>{state.error}</Alert>}

          {state.gate?.kind === 'short' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>This video is probably too short for chapters</AlertTitle>
              {state.gate.message
                || `It runs ${formatTime(state.gate.duration)}, which usually does not need chapters.`}
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="contained" color="warning" onClick={() => run({ register: state.gate.register, force: true })}>
                  Suggest anyway
                </Button>
              </Box>
            </Alert>
          )}

          {state.gate?.kind === 'overwrite' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>This replaces the existing chapters</AlertTitle>
              {state.gate.message}
              <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Current</Typography>
                  <ChapterList chapters={state.gate.existingChapters || []} dense />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Proposed</Typography>
                  <ChapterList chapters={state.gate.chapters || []} dense />
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="contained" color="warning" onClick={() => run({ register: true, force: true })}>
                  Replace them
                </Button>
              </Box>
            </Alert>
          )}

          {state.result && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={state.registered ? 'success' : 'info'}>
                <AlertTitle>
                  {state.registered ? 'Chapters registered' : `${proposed.length} chapters suggested`}
                </AlertTitle>
                {state.result.duration ? `${formatTime(state.result.duration)} of transcript` : null}
                {state.result.source_cues ? `, ${state.result.source_cues} cues` : null}
                {!state.registered && '. Nothing has been written yet.'}
              </Alert>

              {/* A stripped offset means the stored VTT carried a recording
                  timecode; surface it so the bad track can be fixed at source. */}
              {offsetStripped > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This video's subtitle track starts at {offsetStripped}h — the pipeline
                  corrected for it here, but the stored track is still wrong and will show
                  no subtitles in the player.
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                <ChapterList chapters={proposed} />
              </Box>

              {!state.registered && existing.length > 0 && (
                <Chip size="small" color="warning" sx={{ mt: 1 }} label={`Replaces ${existing.length} existing`} />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setState(EMPTY)} disabled={state.loading}>
            {state.registered ? 'Done' : 'Cancel'}
          </Button>
          {!state.registered && (
            <Button onClick={() => run({ register: false })} disabled={state.loading}>
              {state.result ? 'Suggest again' : 'Suggest'}
            </Button>
          )}
          {state.result && !state.registered && (
            <Button variant="contained" onClick={() => run({ register: true })} disabled={state.loading}>
              Register chapters
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default ChapterManager;
